import { useState } from "react";
import { toast } from "sonner";
import { adminShop, errMsg, money } from "../../lib/shopApi";
import { btn, inputCls, SUPPLIER_LABEL } from "./shared";
import { PrintedMintPanel } from "./PrintedMintPanel";

const PrintfulPanel = ({ order, passcode, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const pf = order.printful || {};
  const run = async (fn, msg) => {
    setBusy(true);
    try { onUpdated(await fn(order.id, passcode)); toast(msg); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded border border-[#E7DFD5] bg-white p-4" data-testid="admin-order-printful-panel">
      <p className="font-display text-lg text-[#4B3A2F] mb-1">Printful</p>
      <p className="text-sm" data-testid="admin-order-printful-status">
        Status: <b>{pf.status || "not sent"}</b>{pf.test_draft ? " · TEST DRAFT (not printed, not charged)" : ""}
        {pf.id ? ` · Printful #${pf.id}` : ""}
      </p>
      {pf.error && <p className="text-sm text-[#A3302A] mt-1" data-testid="admin-order-printful-error">{pf.error}</p>}
      {(pf.shipments || []).map((s) => (
        <p key={s.tracking_number} className="text-sm mt-1">Shipped · {s.carrier} · {s.tracking_url ? <a className="underline" href={s.tracking_url} target="_blank" rel="noreferrer">{s.tracking_number}</a> : s.tracking_number}</p>
      ))}
      <div className="flex flex-wrap gap-2 mt-3">
        {pf.dashboard_url && <a href={pf.dashboard_url} target="_blank" rel="noreferrer" className={btn.ghost} data-testid="admin-order-printful-open">Open in Printful</a>}
        {pf.id && <button type="button" className={btn.ghost} disabled={busy} onClick={() => run(adminShop.refreshPrintful, "Updated from Printful.")} data-testid="admin-order-printful-refresh">Check for updates</button>}
        {pf.status === "error" && <button type="button" className={btn.primary} disabled={busy} onClick={() => run(adminShop.retryPrintful, "Sent to Printful again.")} data-testid="admin-order-printful-retry">Retry sending</button>}
      </div>
    </div>
  );
};

export const OrderDetail = ({ order, passcode, onUpdated }) => {
  const a = order.address;
  const addressText = `${a.name}\n${a.address1}${a.address2 ? `\n${a.address2}` : ""}\n${a.city}, ${a.state_code} ${a.zip}\n${a.country_code === "CA" ? "Canada" : "United States"}`;
  return (
    <div className="border-t border-[#E7DFD5] p-5 grid md:grid-cols-2 gap-5" data-testid={`admin-order-detail-${order.order_number}`}>
      <div className="space-y-3">
        <ul className="text-sm space-y-1">
          {order.lines.map((l) => (
            <li key={l.variant_id} className="flex justify-between gap-2">
              <span>{l.title} <span className="text-[#6D5A50]">· {l.variant_label} × {l.quantity} · {SUPPLIER_LABEL[l.supplier]}</span></span>
              <span>{money(l.unit_price * l.quantity)}</span>
            </li>
          ))}
        </ul>
        <div className="text-sm border-t border-[#EFE6D8] pt-2 space-y-0.5">
          <p className="flex justify-between"><span>Shipping</span><span>{money(order.shipping_total)}</span></p>
          <p className="flex justify-between"><span>Sales tax</span><span>{money(order.tax_total)}</span></p>
          <p className="flex justify-between font-semibold"><span>Total paid</span><span>{money(order.amount_total)}</span></p>
        </div>
        <textarea readOnly rows={5} className={inputCls} value={`${addressText}\n${a.email}`} data-testid="admin-order-address" />
        <button type="button" className={btn.ghost} onClick={() => { navigator.clipboard.writeText(addressText); toast("Address copied."); }} data-testid="admin-order-copy-address">Copy address</button>
      </div>
      <div className="space-y-3">
        {order.printful && <PrintfulPanel order={order} passcode={passcode} onUpdated={onUpdated} />}
        {order.printed_mint && <PrintedMintPanel order={order} passcode={passcode} onUpdated={onUpdated} />}
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6D5A50] mb-1">History</p>
          <ul className="text-xs space-y-1" data-testid="admin-order-events">
            {(order.events || []).map((e, i) => (
              <li key={i} className={e.level === "error" ? "text-[#A3302A]" : "text-[#4B3A2F]"}>{new Date(e.at).toLocaleString()} — {e.text}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
